import { Box, Grid, Stack, Typography } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { blue, divider, black } from "@/theme/colors";
import { InnerBox } from "@/components/elements/InnerBox";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEnum } from "@/api/services/global/enum";
import { useGetNationalityList } from "@/api/services/global/master";
import { Select } from "@/components/elements/Select";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import dayjs from "dayjs";
import { TEducationBackgroundEntry } from "@/api/entities/talent.entity";
import { YEAR_MONTH_STR_FORMAT } from "@/constants/datetime";
import { sortNationalityOptions } from "@/features/talent/utils/sortNationalityOptions";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY_ENTRY: TEducationBackgroundEntry = {
  educationLevelId: null,
  schoolName: null,
  countryId: null,
  enrollmentDate: null,
  graduationDate: null,
};

const isEntryEmpty = (entry: TEducationBackgroundEntry | null | undefined): boolean => {
  if (!entry) return true;
  return !entry.educationLevelId && !entry.schoolName?.trim() && !entry.countryId && !entry.enrollmentDate && !entry.graduationDate;
};

type SortableEducationItemProps = {
  fieldId: string;
  index: number;
  totalItems: number;
  control: ReturnType<typeof useFormContext<TTalentForm>>["control"];
  nationalityOptions: { label: string; value: string | number }[];
  getFilteredEducationLevelOptions: (currentIndex: number) => { label: string; value: string | number }[];
  onRemove: (index: number) => void;
  onAdd: (index: number) => void;
  canAdd: boolean;
};

const SortableEducationItem = ({
  fieldId,
  index,
  totalItems,
  control,
  nationalityOptions,
  getFilteredEducationLevelOptions,
  onRemove,
  onAdd,
  canAdd,
}: SortableEducationItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fieldId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <InnerBox mt={3} p={2} pt={0} showDivider={false} borderColor={divider.high} bordered={true}>
        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" mt={2} mb={1} spacing={0.5}>
            <Box {...attributes} {...listeners} sx={{ cursor: "grab", display: "flex", alignItems: "center" }}>
              <DragIndicatorIcon sx={{ color: black[80], fontSize: 24 }} />
            </Box>
            <Typography variant="body14Bold" color={black[30]}>
              学歴{index + 1}
            </Typography>
          </Stack>
        )}

        {totalItems <= 1 && (
          <Stack mt={2} mb={1}>
            <Typography variant="body14Bold" color={black[30]}>
              学歴{index + 1}
            </Typography>
          </Stack>
        )}

        <Stack mt={2}>
          <Grid container rowSpacing={2} columnSpacing={3}>
            <Grid item xs={12}>
              <FormField control={control} name={`educationBackground.${index}.educationLevelId`} label="学歴">
                <Select options={getFilteredEducationLevelOptions(index)} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name={`educationBackground.${index}.schoolName`} label="学校名">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name={`educationBackground.${index}.countryId`} label="国名">
                <Select options={nationalityOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name={`educationBackground.${index}.enrollmentDate`} label="入学年月">
                <InputDatePicker
                  minDate={dayjs("1900-01-01")}
                  maxDate={dayjs()}
                  views={["year", "month"]}
                  openTo="month"
                  format={YEAR_MONTH_STR_FORMAT}
                />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name={`educationBackground.${index}.graduationDate`} label="卒業年月">
                <InputDatePicker minDate={dayjs("1900-01-01")} views={["year", "month"]} openTo="month" format={YEAR_MONTH_STR_FORMAT} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
      </InnerBox>

      <Stack direction="row" alignItems="center" spacing={3} mt={1}>
        {canAdd && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={() => onAdd(index)}>
            <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 18 }} />
            <Typography variant="cap12">学歴を追加</Typography>
          </Stack>
        )}

        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer", width: "fit-content" }} onClick={() => onRemove(index)}>
            <ClearIcon sx={{ color: black[80], fontSize: 18, display: "flex" }} />
            <Typography variant="cap12">削除</Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export const TalentFormEducationFields = () => {
  const { control, trigger, formState, clearErrors, getValues } = useFormContext<TTalentForm>();
  const { data: educationLevelOptions } = useEnum("EEducationLevel");
  const { nationalityOptions } = useGetNationalityList();
  const talentNationalityId = useWatch({ control, name: "nationalityId" });
  const sortedNationalityOptions = useMemo(
    () => sortNationalityOptions(nationalityOptions, talentNationalityId),
    [nationalityOptions, talentNationalityId],
  );
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  const { fields, append, replace } = useFieldArray({
    control,
    name: "educationBackground",
  });

  const educationBackground = useWatch({ control, name: "educationBackground" }) || [];

  // Max entries based on available education level options
  const maxEntries = educationLevelOptions?.length || 10;

  // Filter out already-selected education levels from other entries' options
  const getFilteredEducationLevelOptions = useCallback(
    (currentIndex: number) => {
      const selectedLevels = educationBackground
        .filter((_, i) => i !== currentIndex)
        .map((entry) => Number(entry?.educationLevelId))
        .filter((id) => !isNaN(id) && id > 0);

      return (educationLevelOptions ?? []).filter((option) => !selectedLevels.includes(Number(option.value)));
    },
    [educationBackground, educationLevelOptions],
  );

  useEffect(() => {
    if (fields.length === 0) {
      append({ ...EMPTY_ENTRY });
    }
  }, [fields.length, append]);

  // Re-validate empty row errors when any field changes (only after form submitted)
  const isAddingRef = useRef(false);
  useEffect(() => {
    if (isAddingRef.current) {
      isAddingRef.current = false;
      return;
    }
    if (formState.isSubmitted) {
      trigger("educationBackground");
    }
  }, [educationBackground, trigger, formState.isSubmitted]);

  const handleInsertField = (afterIndex: number) => {
    if (fields.length < maxEntries) {
      isAddingRef.current = true;
      const currentValues = getValues("educationBackground") || [];
      const updated = [...currentValues];
      updated.splice(afterIndex + 1, 0, { ...EMPTY_ENTRY });
      replace(updated);
      setRenderKey((k) => k + 1);
    }
  };

  const handleRemoveField = (index: number) => {
    if (fields.length > 1) {
      const entry = educationBackground[index];
      if (isEntryEmpty(entry)) {
        clearErrors("educationBackground");
        const currentValues = getValues("educationBackground") || [];
        const updated = currentValues.filter((_: unknown, i: number) => i !== index);
        replace(updated);
        setRenderKey((k) => k + 1);
      } else {
        setDeleteIndex(index);
        setIsDeleteModalOpen(true);
      }
    }
  };

  const confirmDelete = () => {
    if (deleteIndex !== null && fields.length > 1) {
      clearErrors("educationBackground");
      const currentValues = getValues("educationBackground") || [];
      const updated = currentValues.filter((_: unknown, i: number) => i !== deleteIndex);
      replace(updated);
      setRenderKey((k) => k + 1);
    }
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const currentValues = getValues("educationBackground") || [];
      const reordered = [...currentValues];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      replace(reordered);
      setRenderKey((k) => k + 1);
    }
  };

  const fieldIds = fields.map((f) => f.id);

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
          {fields.map((field, index) => (
            <SortableEducationItem
              key={`${field.id}-${renderKey}`}
              fieldId={field.id}
              index={index}
              totalItems={fields.length}
              control={control}
              nationalityOptions={sortedNationalityOptions}
              getFilteredEducationLevelOptions={getFilteredEducationLevelOptions}
              onRemove={handleRemoveField}
              onAdd={handleInsertField}
              canAdd={fields.length < maxEntries}
            />
          ))}
        </SortableContext>
      </DndContext>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onOk={confirmDelete}
        message="「学歴」を削除してもよろしいですか？"
      />
    </>
  );
};
